import React, { useEffect, useMemo, useState } from 'react';
import { Page, Document, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import PdfDocumentViewer from '../../../shipments_manager/views/PdfDocumentViewer';
import JsBarcode from 'jsbarcode';
import { CircularProgress, Typography } from '@mui/joy';
import i18n from '../../../../i18n';
import { getShipmentById } from '../../../main/API/shipment';
import { readCustomer } from '../../../main/API/customer';
import { getShipmentPiece } from '../../../main/API/shipmentPiece';
import { getLocalizedUserFullName } from '../../../main/utils/userDisplayName';

/** Printed stickers always use English labels (independent of UI language). */
const tSticker = i18n.getFixedT('en', 'ui');

const STICKER_PAGE_SIZE = [750, 750];
const STICKER_BASE_VALUE_FONT = 26;
const STICKER_MIN_VALUE_FONT = 16;

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    paddingTop: 18,
    paddingHorizontal: 36,
    paddingBottom: 14,
    fontFamily: 'Helvetica',
  },
  scanSection: {
    flexShrink: 0,
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingBottom: 8,
    marginBottom: 8,
  },
  barcodeContainer: {
    width: '100%',
    height: 148,
  },
  barcodeImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  barcodeNumberContainer: {
    width: '100%',
    height: 52,
    marginTop: 2,
  },
  barcodeNumberImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  heroRow: {
    flexDirection: 'row',
    flexShrink: 0,
    alignItems: 'stretch',
    marginBottom: 8,
    minHeight: 72,
  },
  heroPieceBox: {
    width: '34%',
    borderWidth: 2,
    borderColor: '#111827',
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  heroPieceLabel: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  heroPieceValue: {
    fontSize: 44,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
  },
  heroMetaBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    justifyContent: 'center',
  },
  heroMetaLabel: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  heroMetaValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  detailsSection: {
    flexDirection: 'row',
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  detailsColumn: {
    width: '50%',
    paddingRight: 6,
  },
  detailsColumnRight: {
    width: '50%',
    paddingLeft: 6,
    borderLeftWidth: 1,
    borderLeftColor: '#e5e7eb',
  },
  fieldRow: {
    marginBottom: 7,
    minHeight: 46,
  },
  fieldType: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  fieldValue: {
    fontSize: STICKER_BASE_VALUE_FONT,
    fontWeight: 'bold',
    color: '#111827',
    lineHeight: 1.15,
  },
  footerBar: {
    flexShrink: 0,
    marginTop: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: '#f3f4f6',
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
  },
  footerLabel: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#6b7280',
    textTransform: 'uppercase',
    marginRight: 8,
    flexShrink: 0,
  },
  footerValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
    flex: 1,
  },
});

function generateBarcodeImages(value) {
  const code = String(value ?? '');
  const canvas1 = document.createElement('canvas');
  JsBarcode(canvas1, code, {
    margin: 0,
    displayValue: false,
    width: 4.5,
    height: 148,
    fontSize: 32,
  });

  const canvas2 = document.createElement('canvas');
  JsBarcode(canvas2, code, {
    margin: 0,
    displayValue: true,
    width: 0,
    height: 0,
    fontSize: 160,
  });

  return {
    barcode: canvas1.toDataURL(),
    barcodeNumber: canvas2.toDataURL(),
  };
}

/** Trim and ellipsize long strings so PDF rows never blow past the page. */
function displayStickerText(raw, { maxLength = 28, emptyFallback = '—' } = {}) {
  if (raw == null || raw === '') return emptyFallback;
  const text = String(raw).replace(/\s+/g, ' ').trim();
  if (!text) return emptyFallback;
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(1, maxLength - 1))}…`;
}

/** Shrink value font for longer strings — keeps one page without clipping. */
function stickerValueStyle(text, { baseSize = STICKER_BASE_VALUE_FONT } = {}) {
  const len = String(text ?? '').length;
  let size = baseSize;
  if (len > 36) size = baseSize - 10;
  else if (len > 28) size = baseSize - 7;
  else if (len > 22) size = baseSize - 4;
  else if (len > 16) size = baseSize - 2;
  return {
    ...styles.fieldValue,
    fontSize: Math.max(STICKER_MIN_VALUE_FONT, size),
  };
}

function normalizeShipmentId(id) {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : String(id);
}

function resolvePieceShipmentId(piece, lockedShipmentId) {
  if (!piece) return normalizeShipmentId(lockedShipmentId);
  const s = piece.shipment;
  if (s != null && s !== '') {
    if (typeof s === 'object') {
      return normalizeShipmentId(s.id ?? lockedShipmentId);
    }
    return normalizeShipmentId(s);
  }
  return normalizeShipmentId(lockedShipmentId);
}

function resolvePiecesTotal(shipment) {
  if (!shipment || typeof shipment !== 'object') return null;
  if (
    shipment.pieces_status_summary &&
    typeof shipment.pieces_status_summary.total === 'number'
  ) {
    return shipment.pieces_status_summary.total;
  }
  if (Array.isArray(shipment.pieces) && shipment.pieces.length > 0) {
    return shipment.pieces.length;
  }
  if (shipment.packages_quantity != null && shipment.packages_quantity !== '') {
    return Number(shipment.packages_quantity);
  }
  return null;
}

function resolveWeightLabel(piece, shipment) {
  const pieceWeight =
    piece.chargeable_weight_kg ?? piece.volumetric_weight_kg ?? piece.weight_kg;
  if (pieceWeight != null && pieceWeight !== '') {
    return `${pieceWeight} kg`;
  }
  if (shipment && shipment.chargeable_weight != null && shipment.chargeable_weight !== '') {
    return `${shipment.chargeable_weight} kg`;
  }
  if (shipment && shipment.volumetric_weight != null && shipment.volumetric_weight !== '') {
    return `${shipment.volumetric_weight} kg`;
  }
  if (shipment && shipment.weight != null && shipment.weight !== '') {
    return `${shipment.weight} kg`;
  }
  return null;
}

function resolveDimensionsLabel(piece) {
  const w = piece.width_cm;
  const l = piece.length_cm;
  const h = piece.height_cm;
  if (w != null && w !== '' && l != null && l !== '' && h != null && h !== '') {
    return `${w} × ${l} × ${h} cm`;
  }
  return null;
}

function resolveShipmentLabel(shipment, shipmentId) {
  if (!shipment && shipmentId == null) return null;
  const id = shipment?.id ?? shipmentId;
  const tracking = shipment?.tracking_number;
  if (tracking) return `#${id} · ${tracking}`;
  if (id != null) return `#${id}`;
  return null;
}

async function resolveOwner(owner) {
  if (owner == null) return null;
  if (typeof owner === 'object') {
    const hasName =
      owner.first_name != null ||
      owner.last_name != null ||
      owner.first_name_ar != null ||
      owner.last_name_ar != null;
    if (hasName && owner.phone_number != null) return owner;
    const ownerId = owner.id;
    if (ownerId != null) {
      try {
        const res = await readCustomer(ownerId);
        if (res?.data) return { ...owner, ...res.data };
      } catch (_) {
        /* keep partial owner */
      }
    }
    return hasName ? owner : null;
  }
  try {
    const res = await readCustomer(owner);
    if (res?.data) return res.data;
  } catch (_) {
    /* ignore */
  }
  return null;
}

function enrichPiecesFromEmbeddedData(pieces, lockedShipmentId) {
  const list = Array.isArray(pieces) ? pieces.filter((p) => p && p.id) : [];
  if (!list.length) return [];

  return list.map((piece) => {
    const shipmentId = resolvePieceShipmentId(piece, lockedShipmentId);
    const embeddedShipment = typeof piece.shipment === 'object' ? piece.shipment : null;
    const shipment = embeddedShipment;
    const piecesTotal = resolvePiecesTotal(shipment);
    const owner =
      shipment?.owner &&
      typeof shipment.owner === 'object' &&
      !Number.isFinite(Number(shipment.owner))
        ? shipment.owner
        : null;

    return {
      ...piece,
      barcode: piece.barcode || String(piece.id),
      human_label: piece.human_label || null,
      sequence: piece.sequence != null ? piece.sequence : 1,
      piecesTotal,
      shipmentId,
      shipmentLabel: resolveShipmentLabel(shipment, shipmentId),
      owner,
      shipper_reference: shipment?.shipper_reference ?? piece.shipper_reference,
      weightLabel: resolveWeightLabel(piece, shipment),
      dimensionsLabel: resolveDimensionsLabel(piece),
      created_at:
        piece.created_at || piece.last_event_at || piece.modified_at || shipment?.created_at,
    };
  });
}

async function enrichPiecesForStickers(pieces, lockedShipmentId) {
  const list = Array.isArray(pieces) ? pieces.filter((p) => p && p.id) : [];
  if (!list.length) return [];

  const pieceDetailsMap = {};
  await Promise.all(
    list.map(async (p) => {
      try {
        const res = await getShipmentPiece(p.id);
        pieceDetailsMap[p.id] = res?.data || null;
      } catch (_) {
        pieceDetailsMap[p.id] = null;
      }
    })
  );

  const mergedList = list.map((p) => ({
    ...p,
    ...(pieceDetailsMap[p.id] || {}),
  }));

  const shipmentIds = [
    ...new Set(
      mergedList
        .map((p) => resolvePieceShipmentId(p, lockedShipmentId))
        .filter((id) => id != null && id !== '')
    ),
  ];

  const shipmentMap = {};
  await Promise.all(
    shipmentIds.map(async (sid) => {
      const embedded = mergedList.find((p) => {
        const ps = p.shipment;
        return typeof ps === 'object' && normalizeShipmentId(ps.id) === sid;
      });
      const embeddedShipment =
        embedded && typeof embedded.shipment === 'object' ? embedded.shipment : null;

      try {
        const res = await getShipmentById(sid);
        const fetched = res?.data || null;
        const base = fetched || embeddedShipment;
        if (base) {
          const owner = await resolveOwner(base.owner);
          shipmentMap[sid] = { ...base, owner: owner || base.owner };
        } else {
          shipmentMap[sid] = embeddedShipment;
        }
      } catch (_) {
        if (embeddedShipment) {
          const owner = await resolveOwner(embeddedShipment.owner);
          shipmentMap[sid] = { ...embeddedShipment, owner: owner || embeddedShipment.owner };
        } else {
          shipmentMap[sid] = null;
        }
      }
    })
  );

  return mergedList.map((piece) => {
    const shipmentId = resolvePieceShipmentId(piece, lockedShipmentId);
    const embeddedShipment = typeof piece.shipment === 'object' ? piece.shipment : null;
    const fetchedShipment = shipmentId != null ? shipmentMap[shipmentId] : null;
    const shipment = fetchedShipment || embeddedShipment;
    const piecesTotal = resolvePiecesTotal(shipment);
    const owner =
      shipment?.owner &&
      typeof shipment.owner === 'object' &&
      !Number.isFinite(Number(shipment.owner))
        ? shipment.owner
        : null;

    return {
      ...piece,
      barcode: piece.barcode || String(piece.id),
      human_label: piece.human_label || null,
      sequence: piece.sequence != null ? piece.sequence : 1,
      piecesTotal,
      shipmentId,
      shipmentLabel: resolveShipmentLabel(shipment, shipmentId),
      owner,
      shipper_reference: shipment?.shipper_reference ?? piece.shipper_reference,
      weightLabel: resolveWeightLabel(piece, shipment),
      dimensionsLabel: resolveDimensionsLabel(piece),
      created_at: piece.created_at || piece.modified_at || shipment?.created_at,
    };
  });
}

function formatStickerDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (_) {
    return null;
  }
}

function StickerFieldRow({ typeLabel, value, maxLength = 28 }) {
  const display = displayStickerText(value, { maxLength });
  return (
    <View style={styles.fieldRow} wrap={false}>
      <Text style={styles.fieldType}>{typeLabel}</Text>
      <Text style={stickerValueStyle(display)} wrap>
        {display}
      </Text>
    </View>
  );
}

function buildStickerFieldGroups(piece, labels, ownerName, dateLabel, sequenceLabel) {
  const left = [
    {
      typeLabel: labels.customer,
      value: ownerName || tSticker('shipmentPieces.stickerNoName'),
      maxLength: 24,
    },
    {
      typeLabel: labels.phone,
      value: piece.owner?.phone_number || tSticker('shipmentPieces.stickerNoPhone'),
      maxLength: 22,
    },
    {
      typeLabel: labels.weight,
      value: piece.weightLabel || tSticker('shipmentPieces.stickerNoWeight'),
      maxLength: 16,
    },
  ];

  const right = [
    {
      typeLabel: labels.shipment,
      value: piece.shipmentLabel || tSticker('shipmentPieces.stickerNoShipment'),
      maxLength: 26,
    },
    {
      typeLabel: labels.shipperReference,
      value: piece.shipper_reference || tSticker('shipmentPieces.stickerNoShipperRef'),
      maxLength: 24,
    },
    {
      typeLabel: labels.dimensions,
      value: piece.dimensionsLabel || tSticker('shipmentPieces.stickerNoDimensions'),
      maxLength: 22,
    },
  ];

  const heroLabel = displayStickerText(piece.human_label, { maxLength: 32 });
  const footerDate = displayStickerText(dateLabel, {
    maxLength: 40,
    emptyFallback: tSticker('shipmentPieces.stickerNoDate'),
  });

  return { left, right, heroLabel, footerDate, sequenceLabel };
}

function StickerPage({ piece, labels }) {
  const { barcode, barcodeNumber } = generateBarcodeImages(piece.barcode);
  const sequenceLabel =
    piece.piecesTotal != null
      ? `${piece.sequence}/${piece.piecesTotal}`
      : String(piece.sequence);
  const ownerName = piece.owner ? getLocalizedUserFullName(piece.owner, 'en') : null;
  const dateLabel = formatStickerDate(piece.created_at);
  const { left, right, heroLabel, footerDate } = buildStickerFieldGroups(
    piece,
    labels,
    ownerName,
    dateLabel,
    sequenceLabel
  );

  return (
    <Page orientation="landscape" size={STICKER_PAGE_SIZE} style={styles.page} wrap={false}>
      <View style={styles.scanSection}>
        <View style={styles.barcodeContainer}>
          <Image style={styles.barcodeImage} src={barcode} />
        </View>
        <View style={styles.barcodeNumberContainer}>
          <Image style={styles.barcodeNumberImage} src={barcodeNumber} />
        </View>
      </View>

      <View style={styles.heroRow} wrap={false}>
        <View style={styles.heroPieceBox}>
          <Text style={styles.heroPieceLabel}>{labels.sequence}</Text>
          <Text style={styles.heroPieceValue}>{sequenceLabel}</Text>
        </View>
        <View style={styles.heroMetaBox}>
          <Text style={styles.heroMetaLabel}>{labels.pieceLabel}</Text>
          <Text style={stickerValueStyle(heroLabel, { baseSize: 24 })} wrap>
            {heroLabel}
          </Text>
        </View>
      </View>

      <View style={styles.detailsSection} wrap={false}>
        <View style={styles.detailsColumn}>
          {left.map((field) => (
            <StickerFieldRow
              key={field.typeLabel}
              typeLabel={field.typeLabel}
              value={field.value}
              maxLength={field.maxLength}
            />
          ))}
        </View>
        <View style={styles.detailsColumnRight}>
          {right.map((field) => (
            <StickerFieldRow
              key={field.typeLabel}
              typeLabel={field.typeLabel}
              value={field.value}
              maxLength={field.maxLength}
            />
          ))}
        </View>
      </View>

      <View style={styles.footerBar} wrap={false}>
        <Text style={styles.footerLabel}>{labels.date}</Text>
        <Text style={stickerValueStyle(footerDate, { baseSize: 20 })} wrap>
          {footerDate}
        </Text>
      </View>
    </Page>
  );
}

const ShipmentPieceSticker = ({
  pieces,
  lockedShipmentId = null,
  embeddedOnly = false,
}) => {
  const [enrichedPieces, setEnrichedPieces] = useState([]);
  const [loading, setLoading] = useState(false);

  const embeddedEnrichedPieces = useMemo(() => {
    if (!embeddedOnly || !pieces?.length) return [];
    return enrichPiecesFromEmbeddedData(pieces, lockedShipmentId);
  }, [embeddedOnly, pieces, lockedShipmentId]);

  const labels = useMemo(
    () => ({
      pieceLabel: tSticker('shipmentPieces.stickerLabels.pieceLabel'),
      sequence: tSticker('shipmentPieces.stickerLabels.sequence'),
      customer: tSticker('shipmentPieces.stickerLabels.customer'),
      phone: tSticker('shipmentPieces.stickerLabels.phone'),
      shipment: tSticker('shipmentPieces.stickerLabels.shipment'),
      shipperReference: tSticker('shipmentPieces.stickerLabels.shipperReference'),
      weight: tSticker('shipmentPieces.stickerLabels.weight'),
      dimensions: tSticker('shipmentPieces.stickerLabels.dimensions'),
      date: tSticker('shipmentPieces.stickerLabels.date'),
    }),
    [],
  );

  useEffect(() => {
    if (embeddedOnly) return undefined;

    let cancelled = false;

    (async () => {
      if (!pieces?.length) {
        setEnrichedPieces([]);
        return;
      }
      setLoading(true);
      try {
        const next = await enrichPiecesForStickers(pieces, lockedShipmentId);
        if (!cancelled) setEnrichedPieces(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [embeddedOnly, pieces, lockedShipmentId]);

  const resolvedPieces = embeddedOnly ? embeddedEnrichedPieces : enrichedPieces;

  const stickerDocument = useMemo(
    () => (
      <Document>
        {resolvedPieces.map((piece) => (
          <StickerPage key={piece.id} piece={piece} labels={labels} />
        ))}
      </Document>
    ),
    [resolvedPieces, labels],
  );

  if (!embeddedOnly && loading) {
    return (
      <Typography sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Typography>
    );
  }

  if (resolvedPieces && resolvedPieces.length > 0) {
    return (
      <PdfDocumentViewer title={tSticker('shipmentPieces.stickersWindowTitle')}>
        {stickerDocument}
      </PdfDocumentViewer>
    );
  }

  return <Text>{tSticker('shipmentPieces.noPieces')}</Text>;
};

export default ShipmentPieceSticker;
